import {
  AmenitiesAutomotiveTransmissionIcon,
  AmenitiesElectricityPowerIcon,
  AmenitiesPillowIcon,
  AmenitiesWaterfuelSewageTankIcon,
  AmenitiesWaterfuelWatertankIcon,
} from '@outdoorsyco/bonfire';
import { SVGProps } from 'react';
import { useIntl } from 'react-intl';

import { ICONS } from '@/components/switchback/Icon/Icon.constants';

import {
  AMENITIES_FOR_CAMPGROUNDS_KEYS,
  AMENITIES_FOR_LODGING_CAMPSITES_KEYS,
  AMENITIES_FOR_RV_CAMPSITES_KEYS,
  AMENITIES_FOR_STAY_KEYS,
  AMENITIES_FOR_TENT_CAMPSITES_KEYS,
  AMENITIES_KEYS,
} from './amenities';
import { ELocationDetails } from './locationDetails';
import { TRulesFilters } from './moreFilter';

export enum EAmenityGroup {
  BEDROOM = 'bedroom',
  COOKING = 'cooking',
  TEMPERATURE = 'temperature',
  BATHROOM = 'bathroom',
  OTHER = 'other',
  OUTDOOR_SPACE = 'outdoor_space',
  SLEEP = 'sleep',
  OFFICE_AND_ENTERTAINMENT = 'office_and_entertainment',
  SAFETY = 'safety',
  ACCESSIBILITY = 'accessibility',
  CAMPGROUND_UTILITY = 'campground_utility',
  CAMPGROUND_FOOD_AND_BEVERAGE = 'campground_food_and_beverage',
  CAMPGROUND_SERVICES = 'campground_services',
  CAMPGROUND_ENTERTAINMENT = 'campground_entertainment',
  CAMPGROUND_SLEEP = 'campground_sleep',
  CAMPGROUND_COOKING = 'campground_cooking',
  CAMPGROUND_BATHROOM = 'campground_bathroom',
  CAMPGROUND_OUTDOOR_SPACE = 'campground_outdoor_space',
  CAMPGROUND_OFFICE_AND_ENTERTAINMENT = 'campground_office_and_entertainment',
  CAMPGROUND_TEMPERATURE = 'campground_temperature',
  CAMPGROUND_SAFETY = 'campground_safety',
  CAMPGROUND_ACCESSIBILITY = 'campground_accessibility',
  CAMPGROUND_OCCUPANCY = 'campground_occupancy',
  CAMPGROUND_ON_SITE = 'campground_on_site',
  CAMPGROUND_HOOK_UPS = 'campground_hook_ups',
  CAMPGROUND_PARKING = 'campground_parking',
}

export const LINENS_INCLUDED_SLUG = 'linens_included';

export type TAmenitySlug =
  | AMENITIES_KEYS
  | AMENITIES_FOR_STAY_KEYS
  | AMENITIES_FOR_CAMPGROUNDS_KEYS
  | AMENITIES_FOR_RV_CAMPSITES_KEYS
  | AMENITIES_FOR_TENT_CAMPSITES_KEYS
  | AMENITIES_FOR_LODGING_CAMPSITES_KEYS
  | ELocationDetails
  | TRulesFilters
  | 'water_tank'
  | 'accessible'
  | typeof LINENS_INCLUDED_SLUG;

interface TAmenityFilter {
  icon?: ICONS;
  bonfireIcon?: (props: SVGProps<SVGSVGElement>) => JSX.Element;
  label: string;
  slug: TAmenitySlug;
  helpText?: string;
}

export interface IAmenityGroup {
  title: string;
  items: TAmenityFilter[];
}

export enum ECampsiteType {
  RV_SITE = 'rv_site',
  TENT_SITE = 'tent_site',
  LODGING_SITE = 'lodging_site',
  STATIONARY_CAMPER_SITE = 'stationary_camper_site',
}

export const useAmenitiesFilterList = () => {
  const intl = useIntl();
  const vehicleAmenities: Partial<Record<EAmenityGroup, IAmenityGroup>> = {
    [EAmenityGroup.BEDROOM]: {
      title: intl.formatMessage({
        description: 'search/filters: bedroom amenities header',
        defaultMessage: 'Bedroom',
        id: 'gqMeZX',
      }),
      items: [
        {
          bonfireIcon: AmenitiesPillowIcon,
          label: intl.formatMessage({
            description: 'search/filters: amenity linens included',
            defaultMessage: 'Linens included',
            id: '2uhr5K',
          }),
          slug: LINENS_INCLUDED_SLUG,
        },
      ],
    },
    [EAmenityGroup.COOKING]: {
      title: intl.formatMessage({
        description: 'search/filters: cooking amenities header',
        defaultMessage: 'Cooking',
        id: 'kvWkRJ',
      }),
      items: [
        {
          icon: ICONS.FRIDGE,
          label: intl.formatMessage({
            description: 'search/filters: amenity refrigerator',
            defaultMessage: 'Refrigerator',
            id: 'kBUON4',
          }),
          slug: 'refrigerator',
        },
        {
          icon: ICONS.MICROWAVE,
          label: intl.formatMessage({
            description: 'search/filters: amenity microwave',
            defaultMessage: 'Microwave',
            id: 'xaA20Y',
          }),
          slug: 'microwave',
        },
        {
          icon: ICONS.KITCHEN_SINK,
          label: intl.formatMessage({
            description: 'search/filters: amenity kitchen sink',
            defaultMessage: 'Kitchen sink',
            id: 'bQSjnF',
          }),
          slug: 'kitchen_sink',
        },
        {
          icon: ICONS.STOVE,
          label: intl.formatMessage({
            description: 'search/filters: amenity stove',
            defaultMessage: 'Stove',
            id: 'EkIKSe',
          }),
          slug: 'stove',
          helpText: intl.formatMessage({
            description: 'search/filters: amenity stove help text',
            defaultMessage: 'Cook your favorite meals on this compact propane fueled stove',
            id: 'lnOPG/',
          }),
        },
        {
          icon: ICONS.OVEN,
          label: intl.formatMessage({
            description: 'search/filters: amenity oven',
            defaultMessage: 'Oven',
            id: 'fmNCtB',
          }),
          slug: 'oven',
        },
      ],
    },
    [EAmenityGroup.TEMPERATURE]: {
      title: intl.formatMessage({
        description: 'search/filters: temperature amenities header',
        defaultMessage: 'Temperature',
        id: 'GiUELs',
      }),
      items: [
        {
          icon: ICONS.AIR_CONDITIONER,
          label: intl.formatMessage({
            description: 'search/filters: amenity air conditioner',
            defaultMessage: 'Air conditioning',
            id: 'JGipYC',
          }),
          slug: 'air_conditioner',
        },
        {
          icon: ICONS.HEATER,
          label: intl.formatMessage({
            description: 'search/filters: amenity heater',
            defaultMessage: 'Heater',
            id: 'zg5aJi',
          }),
          slug: 'heater',
        },
      ],
    },
    [EAmenityGroup.BATHROOM]: {
      title: intl.formatMessage({
        description: 'search/filters: bathroom amenities header',
        defaultMessage: 'Bathroom',
        id: 'DHneQH',
      }),
      items: [
        {
          icon: ICONS.TOILET,
          label: intl.formatMessage({
            description: 'search/filters: amenity toilet',
            defaultMessage: 'Toilet',
            id: '2ZV9bm',
          }),
          slug: 'toilet',
        },
        {
          icon: ICONS.SHOWER_INSIDE,
          label: intl.formatMessage({
            description: 'search/filters: amenity inside shower',
            defaultMessage: 'Indoor shower',
            id: '5b1eu+',
          }),
          slug: 'inside_shower',
        },
        {
          icon: ICONS.HOT_WATER_FOR_STAY,
          label: intl.formatMessage({
            description: 'search/filters: amenity hot water',
            defaultMessage: 'Hot water',
            id: 'QEvHcx',
          }),
          slug: 'hot_water',
        },
        {
          icon: ICONS.KITCHEN_SINK,
          label: intl.formatMessage({
            description: 'search/filters: amenity bathroom sink',
            defaultMessage: 'Bathroom sink',
            id: 'ubJTXv',
          }),
          slug: 'bathroom_sink',
        },
        {
          icon: ICONS.SHOWER_OUTSIDE,
          label: intl.formatMessage({
            description: 'search/filters: amenity outside shower',
            defaultMessage: 'Outdoor shower',
            id: '6owXFI',
          }),
          slug: 'outside_shower',
          helpText: intl.formatMessage({
            description: 'search/filters: amenity outside shower help text',
            defaultMessage: 'Rinse with fresh water outside of the RV',
            id: 'j1F9h1',
          }),
        },
      ],
    },
    [EAmenityGroup.OTHER]: {
      title: intl.formatMessage({
        description: 'search/filters: other amenities header',
        defaultMessage: 'Other',
        id: '+C25bz',
      }),
      items: [
        {
          icon: ICONS.GENERATOR,
          label: intl.formatMessage({
            description: 'search/filters: amenity generator',
            defaultMessage: 'Generator',
            id: 'deMtrZ',
          }),
          slug: 'generator',
        },
        {
          icon: ICONS.FRESH_WATER,
          label: intl.formatMessage({
            description: 'search/filters: amenity water tank',
            defaultMessage: 'Water tank',
            id: 'Zfsb1t',
          }),
          slug: 'water_tank',
        },
        {
          bonfireIcon: AmenitiesElectricityPowerIcon,
          label: intl.formatMessage({
            description: 'search/filters: amenity electric hookup',
            defaultMessage: 'Electric hookup',
            id: '57Nroo',
          }),
          slug: 'hookup_electric',
        },
        {
          bonfireIcon: AmenitiesWaterfuelWatertankIcon,
          label: intl.formatMessage({
            description: 'search/filters: amenity water hookup',
            defaultMessage: 'Water hookup',
            id: 'jiRA3+',
          }),
          slug: 'hookup_water',
        },
        {
          icon: ICONS.SOLAR,
          label: intl.formatMessage({
            description: 'search/filters: amenity solar',
            defaultMessage: 'Solar panel',
            id: 'Ois6Gb',
          }),
          slug: 'solar',
        },
        {
          bonfireIcon: AmenitiesWaterfuelSewageTankIcon,
          label: intl.formatMessage({
            description: 'search/filters: amenity sewer hookup',
            defaultMessage: 'Sewer hookup',
            id: 'WPuPla',
          }),
          slug: 'hookup_sewer',
        },
        {
          icon: ICONS.LEVELING_JACK,
          label: intl.formatMessage({
            description: 'search/filters: amenity leveling jacks',
            defaultMessage: 'Leveling jacks',
            id: 'z8qLJ+',
          }),
          slug: 'leveling_jacks',
        },
        {
          bonfireIcon: AmenitiesAutomotiveTransmissionIcon,
          label: intl.formatMessage({
            description: 'search/filters: amenity automatic transmission',
            defaultMessage: 'Automatic transmission',
            id: 'lco78r',
          }),
          slug: 'automatic_transmission',
        },
        {
          bonfireIcon: AmenitiesAutomotiveTransmissionIcon,
          label: intl.formatMessage({
            description: 'search/filters: amenity manual transmission',
            defaultMessage: 'Manual transmission',
            id: 'tCOh0L',
          }),
          slug: 'manual_transmission',
        },
      ],
    },
  };

  return vehicleAmenities;
};
